import { useState, useEffect, useRef } from 'react';
import { useIdleTimer } from 'react-idle-timer';
import { INLINE_VARIANT, makeStyles, Dialog, DialogTitle, DialogContent, DialogActions, Button, DialogContentText, Alert, Snackbar } from '@ellucian/react-design-system/core';
import { CONSTANTS, routes } from '../Helpers/Configuration';
import { useIntl } from 'react-intl';
import { useHistory, matchPath } from 'react-router-dom';

const globalWarningState = { current: false };
const TIMEOUT_REDIRECT_PENDING_KEY = 'applicantTimeoutRedirectPending';

const useStyles = makeStyles({
    name: 'Timeout'
})(() => ({
    dialogTitle: {
        padding: 0,
        margin: '-24px -24px 0',
        '& > *': {
            width: '100%'
        },
        '& .MuiSnackbar-root': {
            width: '100%'
        },
        '& .MuiSnackbarContent-root': {
            width: '100%',
            maxWidth: 'none',
            flexGrow: 1
        }
    },
    dialogContentText: {
        textAlign: 'center',
        marginBottom: 0
    }
}));

const Timeout = props => {
    const intl = useIntl();
    const history = useHistory();
    const { classes } = useStyles();

    // Snackbar state
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarVariant, setSnackbarVariant] = useState('info');

    const { sessionTimeout, warningThreshold, countdownDuration } = CONSTANTS.timeout;
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [timeoutCountdown, setTimeoutCountdown] = useState(countdownDuration);
    const countdownIntervalRef = useRef(null);
    const timeoutRedirectPendingRef = useRef(false);
    
    // Flag to track if dialog has been initially announced
    const dialogAnnounced = useRef(false);

    // Function to show snackbar
    const showSnackbar = (message, variant = 'info') => {
        setSnackbarMessage(message);
        setSnackbarVariant(variant);
        setSnackbarOpen(true);
    };

    // Handle snackbar close
    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
    };

    const clearCountdownInterval = () => {
        if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
        }
        globalWarningState.current = false;
        dialogAnnounced.current = false;
    };

    const setPendingTimeoutRedirect = () => {
        timeoutRedirectPendingRef.current = true;
        try {
            sessionStorage.setItem(TIMEOUT_REDIRECT_PENDING_KEY, 'true');
        } catch (_e) {
            // Ignore storage failures and rely on in-memory refs.
        }
    };

    const clearPendingTimeoutRedirect = () => {
        timeoutRedirectPendingRef.current = false;
        try {
            sessionStorage.removeItem(TIMEOUT_REDIRECT_PENDING_KEY);
        } catch (_e) {
            // Ignore storage failures and rely on in-memory refs.
        }
    };

    const getTimeoutRedirectTarget = () => {
        const externalMatch = matchPath(history.location.pathname, {
            path: [routes.externalApplication, routes.applicationShare]
        });
        if (externalMatch) {
            return routes.externalApplication;
        }

        const retrievalMatch = matchPath(history.location.pathname, {
            path: [
                routes.applicationForm,
                routes.newApplication,
                routes.applicationFormSupplementalForm
            ]
        });
        const homeExactMatch = matchPath(history.location.pathname, {
            path: [routes.home],
            exact: true
        });

        if (retrievalMatch || homeExactMatch) {
            return routes.retrieval;
        }

        // Fallback to retrieval when timeout flow cannot determine a route.
        return routes.retrieval;
    };

    const enforcePendingTimeoutRedirect = () => {
        if (!timeoutRedirectPendingRef.current) {
            return;
        }

        const target = getTimeoutRedirectTarget();
        const currentPath = history.location.pathname;

        if (currentPath !== target) {
            history.push(target);
            return;
        }

        clearPendingTimeoutRedirect();
    };

    // Clean up function to ensure dialog elements are properly removed
    const cleanupDialog = () => {
        // Clear any lingering timeouts
        clearCountdownInterval();
        // Reset announcement state
        dialogAnnounced.current = false;
    };

    const handleLogout = () => {
        props.forceTimeout();
        setPendingTimeoutRedirect();

        // First set dialog to closed to remove from DOM
        setIsDialogOpen(false);
        // Clean up all dialog-related elements
        cleanupDialog();
        
        // Give the DOM time to fully update
        setTimeout(() => {
            // Set focus to the container to help screen reader reset its context
            const container = document.getElementById('timeoutContainer');
            if (container) {
                container.focus();
            }
            
            // Now show the snackbar with sufficient delay after dialog is gone
            setTimeout(() => {
                const message = intl.formatMessage({ id: 'timeoutSnackbarMessage' });
                showSnackbar(message, 'info');
                
                // Finally handle the navigation after snackbar has been announced
                setTimeout(() => {
                    handleTimeoutRedirect({ reloadExternal: true });
                    enforcePendingTimeoutRedirect();
                }, 2000);
            }, 500);
        }, 300);
    };

    const handleContinue = () => {
        // First set dialog to closed to remove from DOM
        setIsDialogOpen(false);
        // Clean up all dialog-related elements
        cleanupDialog();
        
        props.handleReset();
        const message = intl.formatMessage({ id: 'timeoutExtendSnackbarMessage' });
        showSnackbar(message, 'success');
        resetIdleTimer();
    };
    
    const handleTimeoutRedirect = ({ reloadExternal = false } = {}) => {
        const target = getTimeoutRedirectTarget();
        if (history.location.pathname !== target) {
            history.push(target);
        }
        if (reloadExternal && target === routes.externalApplication) {
            window.location.reload();
        }
        return target;
    };

    const onIdle = () => {
        const onRetrieval = matchPath(history.location.pathname, {
            path: [routes.retrieval, routes.retrievalSubmitted],
            exact: true
        });

        const onExcludedRoute = matchPath(history.location.pathname, {
            path: [
                routes.invalidShareUrl,
                routes.invalidApplicantLink
            ],
            exact: true
        });
        
        if (!onRetrieval && !onExcludedRoute) {
            if (globalWarningState.current) {
                return; // Prevent duplicate dialog and interval
            }
            globalWarningState.current = true;
            setIsDialogOpen(true);
            let countDown = countdownDuration;
            setTimeoutCountdown(countDown); // Start timeoutCountdown from 30 seconds

            // Reset announcement state for new dialog
            dialogAnnounced.current = false; // Will be set to true after initial render

            const interval = setInterval(() => {
                if (countDown > 0) {
                    countDown--;
                    setTimeoutCountdown(countDown);
                    // No additional announcements during countdown
                } else {
                    // Time is up - handle logout
                    handleLogout();
                }
            }, 1000);

            countdownIntervalRef.current = interval;
        } else {
            props.handleReset();
        }
    };

    // Mark dialog as announced when it opens and set focus on content
    useEffect(() => {
        if (isDialogOpen && !dialogAnnounced.current) {
            // Short delay to ensure the dialog is fully rendered before marking as announced
            const timer = setTimeout(() => {
                // Mark as announced
                dialogAnnounced.current = true;
            }, 300);
            
            return () => clearTimeout(timer);
        }
    }, [isDialogOpen]);

    // Cleanup on unmount
    useEffect(() => {
        try {
            const hasPending = sessionStorage.getItem(TIMEOUT_REDIRECT_PENDING_KEY) === 'true';
            if (hasPending) {
                timeoutRedirectPendingRef.current = true;
                enforcePendingTimeoutRedirect();
            }
        } catch (_e) {
            // Ignore storage failures and rely on in-memory refs.
        }

        const onUserAction = () => {
            enforcePendingTimeoutRedirect();
        };

        const removeListeners = history.listen(location => {
            if (!timeoutRedirectPendingRef.current) {
                return;
            }
            const target = getTimeoutRedirectTarget();
            if (location.pathname === target) {
                clearPendingTimeoutRedirect();
            }
        });

        window.addEventListener('click', onUserAction, true);
        window.addEventListener('keydown', onUserAction, true);
        window.addEventListener('touchstart', onUserAction, true);

        return () => {
            clearCountdownInterval();
            clearPendingTimeoutRedirect();
            removeListeners();

            window.removeEventListener('click', onUserAction, true);
            window.removeEventListener('keydown', onUserAction, true);
            window.removeEventListener('touchstart', onUserAction, true);
        };
    }, [history]);

    // Additional effect to monitor dialog closing
    useEffect(() => {
        if (!isDialogOpen) {
            cleanupDialog();
        }
    }, [isDialogOpen]);

    const { reset: resetIdleTimer } = useIdleTimer({
        timeout: sessionTimeout - warningThreshold, // 30 seconds before timeout dialog appears
        onIdle,
        debounce: 250
    });

    return (
        <div id="timeoutContainer" tabIndex="-1">
            {isDialogOpen && (
                <>
                    <Dialog
                        id="timeoutDialog"
                        open={isDialogOpen}
                        showTitleBarClose={false}
                        disableEnforceFocus={false}
                        role="alertdialog"
                        aria-labelledby="timeoutDialogTitle"
                        aria-describedby="timeoutDialogContent"
                        onClose={() => {
                            setIsDialogOpen(false);
                            cleanupDialog();
                        }}
                    >
                        <DialogTitle id="timeoutDialogTitle" className={classes.dialogTitle}>
                            <Alert
                                alertType="warning"
                                open={true}
                                userDismissable={false}
                                variant={INLINE_VARIANT}
                                text={intl.formatMessage({ id: 'timeoutDialogTitle' })}
                            />
                        </DialogTitle>
                        <DialogContent>
                            <DialogContentText 
                                id="timeoutDialogContent" 
                                className={classes.dialogContentText}
                            >
                                {intl.formatMessage({ id: 'timeoutDialogWarningDescription' }, { timeoutCountdown })}
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            <Button
                                id="resetTimerButton"
                                color="primary"
                                onClick={handleContinue}
                                ref={button => {
                                    if (button && !dialogAnnounced.current) {
                                        button.focus();
                                    }
                                }
                                }
                            >
                                {intl.formatMessage({ id: 'timeoutDialogButton' })}
                            </Button>
                        </DialogActions>
                    </Dialog>
                </>
            )}

            {/* Snackbar for session timeout */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={handleSnackbarClose}
                variant={snackbarVariant}
                message={snackbarMessage}
            />
        </div>
    );
};

export default Timeout;
